import { mathJS } from './mathjs';
import { removeNumberFormatting } from './math.helpers';

/**
 * Derives a finite number from a raw user-input string, or `undefined` when the
 * input can't be interpreted as one. Consumers treat `undefined` as "unparsable"
 * and reset the field as if nothing was typed.
 */
export type NumericInputParser = (raw: string) => number | undefined;

/**
 * A well-formed plain number: an ungrouped integer (`1234`) or digits grouped in
 * threes by spaces/dots/commas (`1 000`, `1.000.000`, `1,234`), with at most one
 * trailing decimal separator. Rejects malformed runs like `3.4.2.4` or `333...3`
 * that {@link removeNumberFormatting} would otherwise coerce into a number.
 */
const FORMATTED_NUMBER = /^-?(?:\d+|\d{1,3}(?:[\s.,]\d{3})+)(?:[.,]\d+)?$/;

/** Plain number with locale formatting stripped (`1 000,5` → `1000.5`). */
const parseFormattedNumber: NumericInputParser = (raw) => {
  if (!FORMATTED_NUMBER.test(raw)) return undefined;
  const normalized = removeNumberFormatting(raw);
  if (normalized === '') return undefined;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : undefined;
};

/** Evaluate {@link expression} with Math.js, returning its value only when that
 *  value is a finite number (a bare comma yields a list/matrix, not a number). */
const evaluateToFiniteNumber = (expression: string): number | undefined => {
  try {
    const result: unknown = mathJS.evaluate(expression);
    return typeof result === 'number' && Number.isFinite(result)
      ? result
      : undefined;
  } catch {
    return undefined;
  }
};

/** Formula fallback: evaluate the expression (`333 * 3`, `1/2`) to its result.
 *  Tries the raw input first so Math.js can read a comma as an argument
 *  separator (`max(1,2)` → 2); only when that produces no finite number does it
 *  retry with decimal commas normalised to dots (`1,4 + 1,4` → 2.8). */
const parseMathExpression: NumericInputParser = (raw) =>
  evaluateToFiniteNumber(raw) ??
  evaluateToFiniteNumber(raw.replace(/(?<=\d),(?=\d)/g, '.'));

/**
 * Ordered parser chain: each strategy is tried until one yields a finite number.
 * Standard parsing first (the common case), then Math.js formula evaluation.
 */
export const DEFAULT_NUMERIC_INPUT_PARSERS: readonly NumericInputParser[] = [
  parseFormattedNumber,
  parseMathExpression,
];

/**
 * Run {@link raw} through {@link parsers} in order, returning the first finite
 * number produced. Returns `undefined` for empty input or when no parser
 * succeeds (e.g. invalid formula) — the caller should then leave the field
 * untouched.
 */
export const parseNumericInput = (
  raw: string,
  parsers: readonly NumericInputParser[] = DEFAULT_NUMERIC_INPUT_PARSERS,
): number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  for (const parse of parsers) {
    const value = parse(trimmed);
    if (value !== undefined) return value;
  }
  return undefined;
};
